import { CircleFragment, RoleSummaryFragment } from '../gql'
import { EntitiesChanges, EntityChangeType } from '../model/log'

interface CircleLookup {
  getCircle(id: string | undefined): CircleFragment | undefined
  getRole(id: string | undefined): RoleSummaryFragment | undefined
  ownerCircleOf(circle: CircleFragment): CircleFragment | undefined
  circleOfRole(roleId: string): CircleFragment | undefined
}

// Which circle a set of changes engages (Yocoop, 09/08/2026).
//
// Same rule as everywhere else: a change is decided by the circle CONTAINING
// what changes, so the answer is `ownerCircleOf` of the affected entity. Two
// nested candidates cannot be told apart by "is it inside" — everything inside
// Admin is also inside the circle above it — only by who owns it directly.
//
// Used to settle the deciding circle of a link tension from what the author
// actually does, rather than asking them to designate it.
export function getChangesDecidingCircleId(
  orgData: CircleLookup,
  changes: EntitiesChanges
): string | undefined {
  const ownerOf = (circleId: string | undefined | null) => {
    const circle = orgData.getCircle(circleId || undefined)
    return circle ? orgData.ownerCircleOf(circle)?.id : undefined
  }

  for (const change of changes.circles ?? []) {
    // A circle being created does not exist yet to be looked up, but the circle
    // it lands in is exactly the one gaining a child, hence the one deciding.
    if (change.type === EntityChangeType.Create) {
      const parentId = change.data.parentId
      if (parentId) return parentId
      continue
    }
    const owner = ownerOf(change.id)
    if (owner) return owner
  }

  // Changing a role changes it for the circle carrying it.
  for (const change of changes.roles ?? []) {
    const circle = orgData.circleOfRole(change.id)
    if (circle) {
      const owner = orgData.ownerCircleOf(circle)?.id
      if (owner) return owner
    }
  }

  for (const change of changes.circlesMembers ?? []) {
    const circleId =
      change.type === EntityChangeType.Update
        ? undefined
        : change.data.circleId
    const owner = ownerOf(circleId)
    if (owner) return owner
  }

  for (const change of changes.circlesLinks ?? []) {
    const parentId =
      change.type === EntityChangeType.Update
        ? undefined
        : change.data.parentId
    const owner = ownerOf(parentId)
    if (owner) return owner
  }

  return undefined
}
